import React, { useState, useEffect } from 'react';

import i18n from 'i18n-react';
import { baseAppId, storageKeys } from 'settings';
import { APIHandler, StorageHandler, SharingPopup, Modal, Diploma } from 'C4';
import { renderToString } from 'react-dom/server';

import badges from 'assets/images/badges';

import BadgeDisplay from 'components/BadgeDisplay';
import ContentHeader from 'components/ContentHeader';
import RewardMenu from 'components/RewardMenu';

import { usePointsHandler } from 'hooks/usePointsHandler';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const params = new URLSearchParams(window.location.search);
let mobile = false;
if (params.get('mobile')) {
  mobile = true;
}

const fancyOptions = (url, title) => ({
  infobar: true,
  thumbs: {
    autoStart: true,
    axis: 'x',
  },
  baseClass: 'customBox',
  buttons: ['thumbs', 'share', 'close'],
  share: {
    tpl: renderToString(
      <SharingPopup
        url={url}
        title=""
        image="https://imgtravpromobile.imgix.net/1795/1711474783-1691516053-generalbackground.jpg?fit=clip&w=1500&h=auto"
      />
    ),
  },
  caption: '',
});

const getTotalPages = (res) => {
  let totalPages = 0;
  if (res && res.data && res.data[0]) {
    const { chapters } = res.data[0];
    // Limit to the first eight chapters
    chapters.slice(0, 8).forEach((chapter) => {
      totalPages += chapter.total_pages - 1;
    });
  }
  return totalPages;
};

// Makes sure progress is returned in percentage value
const calculateProgress = (progress, pages) =>
  Math.floor((progress / pages) * 100);

const getTotalProgress = (res) => {
  let totalProgress = 0;
  if (res && res.data && res.data[0]) {
    const { chapters } = res.data[0];
    // Limit to the first eight chapters
    chapters.slice(0, 8).forEach((chapter) => {
      totalProgress += chapter.progress;
    });
  }
  return totalProgress;
};

/**
 * BadgeView
 *
 * A view component that displays the diploma and each attained badge for the user. These badges being:
 *
 * - Bronze/Silver/Gold/Platinum badges: Based on the amount of completed trainings
 * - Specialization badges
 * - Partner Badges
 *
 * @returns {JSX.Element} A component that displays the user's badges and progress.
 */
const BadgeView = () => {
  const [chapters, setChapters] = useState([]);
  const [subApps, setSubApps] = useState([]);
  const [totalProgressValue, setTotalProgressValue] = useState(0);
  const [showDiploma, setShowDiploma] = useState(false);
  const [diplomaImage, setDiplomaImage] = useState('');

  const {
    appId,
    lang: { langFile },
    loaded: settingsLoaded,
  } = useSettings();
  const {
    user: { first_name: firstName, last_name: lastName, email },
    loaded: userInfoLoaded,
  } = useUserInfo();
  const {
    tiers,
    completedItems,
    totalPoints,
    getTieredValue,
    loaded: loadedPointsHandler,
    refreshData,
  } = usePointsHandler();

  const { tierBadges } = badges;
  const tierIndex = tiers && Math.min(getTieredValue(), tiers.length - 1);
  const currentPath = window.location.pathname;

  const handleDiplomaOpen = () => setShowDiploma(true);
  const handleDiplomaClose = () => setShowDiploma(false);

  useEffect(() => {
    const image = `https://cms.travpromobile.com/api/app/diploma?first_name=${firstName}&last_name=${lastName}&app_id=${appId}&lang=${langFile}_${langFile
      .replace('pt', 'br')
      .replace('ko', 'kr')
      .toUpperCase()}`;
    setDiplomaImage(image);
  }, [firstName, lastName, appId, langFile]);

  useEffect(() => {
    const getUserInfo = () => {
      StorageHandler.load(storageKeys.user).then((res) => {
        if (res) {
          APIHandler.getUserProgress(appId, email).then((userProgress) => {
            if (userProgress) {
              const totalProgress = getTotalProgress(userProgress);
              const totalPages = getTotalPages(userProgress);
              const percentage = calculateProgress(totalProgress, totalPages);
              setTotalProgressValue(percentage >= 100 ? 100 : percentage);
            }
          });
          // Refresh points data after successful login
          refreshData();
        } else {
          window.location.href = '/logout';
          setTotalProgressValue(0);
        }
      });

      StorageHandler.load(storageKeys.accessToken).then((res) => {
        if (!res) {
          window.location.href = '/logout';
        }
      });
    };

    const getSubAppData = (appIds) => {
      if (appIds.apps.length > 0) {
        return APIHandler.getUserProgress(appIds.apps, email).then(
          (userProgress) => {
            if (userProgress) {
              const progress = Object.values(userProgress.data).map((app) => {
                const [chapter] = app.chapters;
                const { appIcon, name, badge } = app;
                return {
                  chapter,
                  name,
                  appIcon,
                  badge,
                };
              });
              return progress;
            }
          }
        );
      }
    };

    const getSubApps = async () => {
      const ids = await APIHandler.getSubApps(baseAppId);
      if (ids) {
        const data = await getSubAppData(ids);
        if (data) {
          setSubApps(data);
        }
      }
    };

    const getChaptersAndProgress = () => {
      APIHandler.getChaptersAndProgress(appId, email).then((data) => {
        setChapters(data.chapters.uncategorized[0].chapters);
      });
    };

    if (settingsLoaded && userInfoLoaded) {
      getChaptersAndProgress();
      getSubApps();
      getUserInfo();
    }
  }, [settingsLoaded, userInfoLoaded]);

  return (
    <div className="contentContainer badgeView">
      {currentPath === '/main/rewards/achievements' && <RewardMenu />}
      <ContentHeader
        header={i18n.translate('badgeView.title')}
        subtitle={i18n.translate('badgeView.subtitle')}
      />

      <hr />

      <div className="content-box">
        {loadedPointsHandler &&
          tiers && ( // Check if the data has been loaded and tiers are available
            <div className="badgeContainer points__container">
              <ContentHeader
                header={i18n.translate('badgeView.badgeDisplayTitle')}
              />
              <BadgeDisplay
                tierBadges={tierBadges}
                tiers={tiers}
                tierIndex={tierIndex}
                totalPoints={totalPoints}
                layout="longBar"
                hideBadge
                completedItems={completedItems}
                hasPopout
              />
              <div className="badgeView__title--tiers">
                {tiers.map((tier, index) => (
                  <div
                    key={tier.title}
                    className={`tier-title ${
                      tierIndex === index ? 'active' : ''
                    } ${tier.completedItems >= tier.count ? '' : 'disabled'}`}
                  >
                    <img
                      className="badge"
                      src={badges.tierBadges[index]}
                      alt="Logo"
                    />

                    <span className="title">
                      {Math.min(tier.completedItems, tier.count)}
                      {' / '}
                      {tier.count} {i18n.translate('badgeView.complete')}
                    </span>
                  </div>
                ))}
              </div>
              <div className="badge-display__wrapper badgeView-text__wrapper">
                {totalProgressValue >= 100 && (
                  <h1 className="badgeView__text">{tiers[tierIndex].copy}</h1>
                )}
              </div>
            </div>
          )}
        {loadedPointsHandler && tiers && totalProgressValue >= 100 && (
          <div className="tier-rewards">
            <hr />
            <ContentHeader header={i18n.translate('badgeView.tierRewards')} />
            {tiers[0].completedItems === tiers[0].count && (
              <div className="tier-rewards__reward">
                <img className="badge" src={badges.tierBadges[0]} alt="Logo" />
                {!mobile && (
                  <h1 className="tier-rewards__text">
                    {i18n.translate('badgeView.click')}{' '}
                    <a
                      href="https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Bronze+Status+Reward.pdf"
                      data-fancybox
                      data-options={JSON.stringify(
                        fancyOptions(
                          'https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Bronze+Status+Reward.pdf',
                          i18n.translate('badgeView.bronzeReward')
                        )
                      )}
                    >
                      {i18n.translate('badgeView.here')}
                    </a>{' '}
                    {i18n.translate('badgeView.bronzeReward')}
                  </h1>
                )}
                {mobile && (
                  <h1 className="tier-rewards__text">
                    {i18n.translate('badgeView.click')}{' '}
                    <a
                      onClick={() =>
                        window.ReactNativeWebView.postMessage(
                          `pdf:https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Bronze+Status+Reward.pdf`
                        )
                      }
                    >
                      {i18n.translate('badgeView.here')}
                    </a>{' '}
                    {i18n.translate('badgeView.bronzeReward')}
                  </h1>
                )}
              </div>
            )}
            {tiers[1].completedItems === tiers[1].count && (
              <div className="tier-rewards__reward">
                <img className="badge" src={badges.tierBadges[1]} alt="Logo" />
                {!mobile && (
                  <h1 className="tier-rewards__text">
                    {i18n.translate('badgeView.click')}{' '}
                    <a
                      href="https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Silver+Status+Reward.pdf"
                      data-fancybox
                      data-options={JSON.stringify(
                        fancyOptions(
                          'https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Bronze+Status+Reward.pdf',
                          i18n.translate('badgeView.bronzeReward')
                        )
                      )}
                    >
                      {i18n.translate('badgeView.here')}
                    </a>{' '}
                    {i18n.translate('badgeView.silverReward')}
                  </h1>
                )}
                {mobile && (
                  <h1 className="tier-rewards__text">
                    {i18n.translate('badgeView.click')}{' '}
                    <a
                      onClick={() =>
                        window.ReactNativeWebView.postMessage(
                          `pdf:https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Silver+Status+Reward.pdf`
                        )
                      }
                    >
                      {i18n.translate('badgeView.here')}
                    </a>{' '}
                    {i18n.translate('badgeView.silverReward')}
                  </h1>
                )}
              </div>
            )}
            {tiers[2].completedItems >= tiers[2].count && (
              <div className="tier-rewards__reward">
                <img className="badge" src={badges.tierBadges[2]} alt="Logo" />
                {!mobile && (
                  <h1 className="tier-rewards__text">
                    {i18n.translate('badgeView.click')}{' '}
                    <a
                      href="https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Gold+Status+Reward.pdf"
                      data-fancybox
                      data-options={JSON.stringify(
                        fancyOptions(
                          'https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Gold+Status+Reward.pdf',
                          i18n.translate('badgeView.goldReward')
                        )
                      )}
                    >
                      {i18n.translate('badgeView.here')}
                    </a>{' '}
                    {i18n.translate('badgeView.goldReward')}
                  </h1>
                )}
                {mobile && (
                  <h1 className="tier-rewards__text">
                    {i18n.translate('badgeView.click')}{' '}
                    <a
                      onClick={() =>
                        window.ReactNativeWebView.postMessage(
                          `pdf:https://front.travpromobile.com/1795/2025%20-%20Travel+Nevada+-+BBI+-+Gold+Status+Reward.pdf`
                        )
                      }
                    >
                      {i18n.translate('badgeView.here')}
                    </a>{' '}
                    {i18n.translate('badgeView.goldReward')}
                  </h1>
                )}
              </div>
            )}
          </div>
        )}

        {totalProgressValue >= 100 && (
          <div>
            <hr />
            <ContentHeader header={i18n.translate('badgeView.diploma')} />
            {/* <ContentHeader header={i18n.translate("badgeView.diploma")} /> */}
            {!mobile && (
              <div className="diploma__container" onClick={handleDiplomaOpen}>
                <img
                  className="diploma__image"
                  src={diplomaImage}
                  alt="diploma"
                />
              </div>
            )}
            {!mobile && (
              <Modal
                show={showDiploma}
                onHide={handleDiplomaClose}
                bgClose
                autosize
              >
                <Diploma
                  appId={appId}
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  key={firstName}
                />
              </Modal>
            )}
            {mobile && (
              <a
                onClick={() =>
                  window.ReactNativeWebView.postMessage(`image:${diplomaImage}`)
                }
                className="mobileDiploma"
              >
                <Diploma
                  appId={appId}
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  key={firstName}
                />
              </a>
            )}
          </div>
        )}
        {chapters.filter(
          (chapter) => chapter.percentage === 100 && chapter.badge !== null
        ).length > 0 && (
          <div>
            <hr />
            <ContentHeader header={i18n.translate('badgeView.chapterBadges')} />
            <div className="badges__collection">
              {chapters.map((chapter) => {
                const { id, percentage, badge } = chapter;
                if (percentage === 100 && badge && !mobile) {
                  return (
                    <a
                      key={id}
                      href={`https://stack.travpromobile.com/download.php?url=${badge}`}
                    >
                      <button
                        className="chapter-badge"
                        style={{
                          backgroundImage: `url(${`${badge.replace(
                            'https://s3.amazonaws.com/developertool',
                            'https://travprocdn.imgix.net/'
                          )}?w=450&fit=clamp`})`,
                        }}
                      />
                    </a>
                  );
                }
                if (percentage === 100 && badge && mobile) {
                  return (
                    <a
                      onClick={() =>
                        window.ReactNativeWebView.postMessage(`image:${badge}`)
                      }
                    >
                      <button
                        className="chapter-badge"
                        style={{
                          backgroundImage: `url(${`${badge.replace(
                            'https://s3.amazonaws.com/developertool',
                            'https://travprocdn.imgix.net/'
                          )}?w=450&fit=clamp`})`,
                        }}
                      />
                    </a>
                  );
                }
              })}
            </div>
          </div>
        )}
        {subApps.filter(
          (subapp) =>
            subapp.chapter.progress >= subapp.chapter.total_pages - 1 &&
            subapp.badge !== null
        ).length > 0 && (
          <div>
            <hr />
            <ContentHeader header={i18n.translate('badgeView.partnerBadges')} />
            <div className="badges__collection">
              {subApps.map((subapp) => {
                const {
                  chapter: { progress, total_pages: totalPages },
                  badge,
                } = subapp;
                if (progress >= totalPages - 1 && badge) {
                  return (
                    <a
                      href={`https://stack.travpromobile.com/download.php?url=${badge}`}
                    >
                      <button
                        className="subapp-badge"
                        style={{
                          backgroundImage: `url(${`${badge.replace(
                            'https://s3.amazonaws.com/developertool',
                            'https://travprocdn.imgix.net/'
                          )}?w=450&fit=clamp`})`,
                        }}
                      />
                    </a>
                  );
                }
              })}
            </div>
          </div>
        )}
      </div>
      {mobile && (
        <style>
          {
            '\
      #diploma img { max-width: 680px; object-fit: contain; width: 100% !important; height: auto !important; } .contentContainer { width: 100% !important; }  .buttons__diploma { display: none !important; } .sharebox__buttons { display: none !important; } .contentContainer { margin-top: 5rem; } .header { display: none; } .cookieConsent { display: none; }'
          }
        </style>
      )}
    </div>
  );
};

export default BadgeView;
